import { logClientError } from 'src/util/server-log';
import { Injectable, inject } from "@angular/core";
import { Router } from '@angular/router';
import * as PriceProto from "@proto/common/price";
import * as PlanProto from "@proto/plans/plan";
import * as ResourceProto from "@proto/plans/resource";
import * as SubscriptionsProto from "@proto/subscriptions/subscriptions";
import * as PaymentProto from "@proto/payments/payments";
import * as SubscriptionMemberProto from "@proto/subscriptions/members";
import { SharedService } from "@services/shared/shared.service";
import { AuthorizationInterceptor } from "src/app/interceptors/interceptor";
import { withPaymentDeadline } from "src/util/payment-call";

import { endpointBaseGRPC } from "src/main";

const organizationId = "e605dc0d-38da-4bc1-a688-b4f3a7ca48f7";

export interface PlanListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: string;
  name?: string[];
  status?: PlanProto.plans.v1.Status[];
  visibility?: PlanProto.plans.v1.Visibility[];
}

export interface PlanForm {
  id?: string;
  name: string;
  description: string;
  price: number;
  cycle: string;
  visibility: string;
  status: string;
  products: PlanProto.plans.v1.PlanProduct[];
  paymentMethods: {
    pix: boolean;
    creditCard: boolean;
    invoice: boolean;
    debit: boolean;
  };
  autoRenewal: boolean;
  maxChargeAttempts: number;
  overdueDays: number;
  trialDays: number;
}

@Injectable({
  providedIn: "root",
})
export class GrpcSubscriptionService {
  private readonly router = inject(Router);

  private clientPlan = new PlanProto.plans.v1.PlanServiceClient(endpointBaseGRPC + "/subscriptions", undefined, {
    unaryInterceptors: [new AuthorizationInterceptor()],
  });
  private clientSubscription = new SubscriptionsProto.subscriptions.v1.SubscriptionServiceClient(
    endpointBaseGRPC + "/subscriptions",
    undefined,
    {
      unaryInterceptors: [new AuthorizationInterceptor()],
    },
  );

  private clientSubscriptionMember = new SubscriptionMemberProto.subscriptions.v1.SubscriptionMemberServiceClient(
    endpointBaseGRPC + "/subscriptions",
    undefined,
    {
      unaryInterceptors: [new AuthorizationInterceptor()],
    },
  );

  private _currentSubscription: SubscriptionsProto.subscriptions.v1.Subscription;

  private _currentPlan: PlanProto.plans.v1.Plan;

  constructor(private sharedService: SharedService) {}

  get currentSubscription() {
    return this._currentSubscription;
  }

  set currentSubscription(subscription: SubscriptionsProto.subscriptions.v1.Subscription) {
    this._currentSubscription = subscription;
  }

  get currentPlan() {
    return this._currentPlan;
  }

  set currentPlan(plan: PlanProto.plans.v1.Plan) {
    this._currentPlan = plan;
  }

  private clientResource = new ResourceProto.plans.v1.ResourcesServiceClient(
    endpointBaseGRPC + "/subscriptions",
    undefined,
    {
      unaryInterceptors: [new AuthorizationInterceptor()],
    },
  );

  getCurrentSubscription() {
    const id = this.sharedService.user?.id || this.sharedService.user?.sub;
    const request = new SubscriptionsProto.subscriptions.v1.GetCurrentSubscriptionRequest({ id });
    return this.clientSubscription.GetCurrentSubscription(request, {});
  }

  /**
   * Obtém a subscription ativa do usuário (status = ACTIVE).
   * Diferente de getCurrentSubscription que retorna a última subscription (pode estar pendente).
   */
  getActiveSubscription() {
    const request = new SubscriptionsProto.subscriptions.v1.GetActiveSubscriptionRequest({});
    return this.clientSubscription.GetActiveSubscription(request, {});
  }

  getMyOffers(planIds: string[] = [], options: { redirectOnAuthError?: boolean } = {}) {
    const request = new SubscriptionsProto.subscriptions.v1.GetMyOffersRequest({ planIds });
    const call = this.clientSubscription.GetMyOffers(request, {});

    return options.redirectOnAuthError === false ? call : this.handleCampaignAuth(call);
  }

  getCancellationOffer() {
    const request = new SubscriptionsProto.subscriptions.v1.GetCancellationOfferRequest({});
    return this.clientSubscription.GetCancellationOffer(request, {});
  }

  acceptCancellationOffer(campaignId: string, planId?: string) {
    const request = new SubscriptionsProto.subscriptions.v1.AcceptCancellationOfferRequest({ campaignId, planId });
    return this.clientSubscription.AcceptCancellationOffer(request, {});
  }

  getMyBenefits() {
    const request = new SubscriptionsProto.subscriptions.v1.GetMyBenefitsRequest({});
    return this.handleCampaignAuth(this.clientSubscription.GetMyBenefits(request, {}));
  }

  private async handleCampaignAuth<T>(request: Promise<T>): Promise<T> {
    try {
      return await request;
    } catch (error: unknown) {
      if (typeof error === 'object' && error !== null && 'code' in error &&
          [7, 16, 401, 403].includes(Number(error.code))) {
        await this.router.navigate(['/entrar']);
      }
      throw error;
    }
  }

  /**
   * Lista todas as subscriptions do usuário atual.
   * Usado para encontrar a subscription ativa (STATUS_ACTIVE = 2).
   */
  listSubscriptions() {
    const request = new SubscriptionsProto.subscriptions.v1.ListSubscriptionRequest({
      userId: this.sharedService.user?.id || this.sharedService.user?.sub,
    });
    return this.clientSubscription.ListSubscription(request, {});
  }

  getPlans(params?: PlanListParams) {
    const request = new PlanProto.plans.v1.ListPlansRequest({
      page: params?.page,
      pageSize: params?.pageSize,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      name: params?.name,
      status: params?.status,
      visibility: params?.visibility,
    });
    return this.clientPlan.ListPlans(request, {});
  }

  getPlanById(id: string) {
    if (!id) {
      logClientError("No plan ID provided");
      return Promise.reject("No plan ID provided");
    }
    const request = new PlanProto.plans.v1.GetPlanRequest({
      id: id,
    });
    return this.clientPlan.GetPlan(request, {});
  }

  getInviteById(id: string) {
    const request = new SubscriptionsProto.subscriptions.v1.GetSubscriptionInvitationRequest({
      invitationId: id,
    });
    return this.clientSubscription.GetSubscriptionInvitation(request, {});
  }

  getDefaultPayment() {
    const request = new SubscriptionsProto.subscriptions.v1.GetDefaultPaymentRequest({});
    return this.clientSubscription.GetDefaultPayment(request, {});
  }

  updateDefaultPaymentToPix() {
    const pix = new SubscriptionsProto.subscriptions.v1.PaymentTypePIX({ enable: true });
    const request = new SubscriptionsProto.subscriptions.v1.UpdateDefaultPaymentRequest({ pix });
    return this.clientSubscription.UpdateDefaultPayment(request, {});
  }

  updateDefaultPaymentToCard(cardId: string, installments: number = 1) {
    const creditCard = new SubscriptionsProto.subscriptions.v1.PaymentTypeCreditCard({
      enable: true,
      cardId,
      installments,
    });
    const request = new SubscriptionsProto.subscriptions.v1.UpdateDefaultPaymentRequest({ creditCard });
    return this.clientSubscription.UpdateDefaultPayment(request, {});
  }

  deactivateSubscription(reason?: string, subjectId?: string) {
    const subscription = this.sharedService.currentSubscription;
    const userId = this.sharedService.user?.id || this.sharedService.user?.sub;

    if (!subscription || !subscription.id) {
      logClientError("No subscription found");
      return Promise.reject("No subscription found");
    }

    if (!userId) {
      logClientError("No user ID found");
      return Promise.reject("No user ID found");
    }

    const request = new SubscriptionsProto.subscriptions.v1.DeactivateSubscriptionRequest({
      reason,
      subjectId,
    });

    return this.clientSubscription.DeactivateSubscription(request, {});
  }

  removeMember(id: string) {
    const subscription = this.sharedService.currentSubscription;
    const request = new SubscriptionMemberProto.subscriptions.v1.RemoveSubscriptionMemberRequest({
      subscriptionId: subscription.id,
      memberId: id,
    });
    return this.clientSubscriptionMember.RemoveSubscriptionMember(request, {});
  }

  removeInvite(id: string) {
    const request = new SubscriptionsProto.subscriptions.v1.CancelSubscriptionInvitationRequest({
      invitationId: id,
    });
    return this.clientSubscription.CancelSubscriptionInvitation(request, {});
  }

  upgradeSubscription(planId: string, paymentType?: PaymentProto.payments.v1.PaymentType) {
    const request = new SubscriptionsProto.subscriptions.v1.UpgradePlanRequest({
      planId,
      ...(paymentType ? { paymentType } : {}),
    });
    return this.clientSubscription.UpgradePlan(request, withPaymentDeadline());
  }

  /**
   * Constrói o PaymentType (proto payments.v1) a partir do método de pagamento
   * padrão do usuário, para ser enviado em requisições que aceitam pagamento explícito
   * (ex.: UpgradePlanRequest), de forma análoga ao que é feito no checkout convencional.
   */
  buildPaymentTypeFromDefault(defaultPayment: {
    pix?: unknown;
    creditCard?: { cardId?: string; installments?: number };
  } | null | undefined): PaymentProto.payments.v1.PaymentType | undefined {
    if (!defaultPayment) {
      return undefined;
    }

    if (defaultPayment.creditCard?.cardId) {
      return new PaymentProto.payments.v1.PaymentType({
        card: new PaymentProto.payments.v1.CardPaymentType({
          id: defaultPayment.creditCard.cardId,
          installments: defaultPayment.creditCard.installments || 1,
        }),
      });
    }

    if (defaultPayment.pix) {
      return new PaymentProto.payments.v1.PaymentType({
        pix: new PaymentProto.payments.v1.PIXPaymentType({}),
      });
    }

    return undefined;
  }

  downgradeSubscription(planId: string) {
    const request = new SubscriptionsProto.subscriptions.v1.DowngradePlanRequest({
      planId,
    });
    return this.clientSubscription.DowngradePlan(request, {});
  }

  requestPayment() {
    const request = new SubscriptionsProto.subscriptions.v1.RequestPaymentRequest({});
    return this.clientSubscription.RequestPayment(request, withPaymentDeadline());
  }

  updateSubscription(paymentType: PaymentProto.payments.v1.PaymentType) {
    const request = new SubscriptionsProto.subscriptions.v1.UpdateSubscriptionRequest({
      paymentType,
    });
    return this.clientSubscription.UpdateSubscription(request, {});
  }

  listResources() {
    const request = new ResourceProto.plans.v1.ListResourcesRequest({});
    return this.clientResource.ListResources(request, {});
  }

  inviteMember(email: string): Promise<SubscriptionMemberProto.subscriptions.v1.InviteSubscriptionMemberResponse> {
    const subscription = this.sharedService.currentSubscription;
    if (!subscription) {
      logClientError("No subscription found");
      return Promise.reject("No subscription found");
    }
    const request = new SubscriptionMemberProto.subscriptions.v1.InviteSubscriptionMemberRequest({
      subscriptionId: subscription.id,
      email,
    });
    return this.clientSubscriptionMember.InviteSubscriptionMember(request, {});
  }

  listMembers() {
    const subscription = this.sharedService.currentSubscription;
    const request = new SubscriptionMemberProto.subscriptions.v1.ListSubscriptionMembersRequest({
      subscriptionId: subscription.id,
    });
    return this.clientSubscriptionMember.ListSubscriptionMembers(request, {});
  }

  getMemberById(id: string) {
    const request = new SubscriptionMemberProto.subscriptions.v1.GetSubscriptionMemberRequest({
      id: id,
    });
    return this.clientSubscriptionMember.GetSubscriptionMember(request, {});
  }

  createPlan(planForm: PlanForm) {
    const planRequest = this.planToCreateRequest(planForm);
    const request = new PlanProto.plans.v1.CreatePlanRequest(planRequest);
    return this.clientPlan.CreatePlan(request, {});
  }

  updatePlan(id: string, planForm: PlanForm) {
    const planRequest = this.planToUpdateRequest(planForm) as any;
    const request = new PlanProto.plans.v1.UpdatePlanRequest({
      ...planRequest,
      organizationId,
      id,
    });
    return this.clientPlan.UpdatePlan(request, {});
  }

  deletePlan(id: string) {
    const request = new PlanProto.plans.v1.DeletePlanRequest({
      id: id,
    });
    return this.clientPlan.DeletePlan(request, {});
  }

  private planToCreateRequest(planForm: PlanForm): {
    name?: string;
    description?: string;
    price?: PriceProto.common.v1.Price;
    cycle?: PlanProto.plans.v1.Cycle;
    visibility?: PlanProto.plans.v1.Visibility;
    status?: PlanProto.plans.v1.Status;
    products?: PlanProto.plans.v1.PlanProdRequest[];
    paymentMethods?: PlanProto.plans.v1.PaymentMethod;
    autoRenewal?: boolean;
    maxChargeAttempts?: number;
    overdueDays?: number;
    trialDays?: number;
    organizationId?: string;
  } {
    return {
      name: planForm.name,
      description: planForm.description,
      price: new PriceProto.common.v1.Price({
        amount: Math.round(planForm.price * 100), // Convert to cents
        currency: "BRL",
      }),
      cycle: this.getCycle(planForm.cycle),
      visibility: this.getVisibility(planForm.visibility),
      status: this.getStatus(planForm.status),
      products: this.getProducts(planForm.products),
      paymentMethods: this.getPaymentMethods(planForm.paymentMethods),
      autoRenewal: planForm.autoRenewal,
      maxChargeAttempts: planForm.maxChargeAttempts,
      overdueDays: planForm.overdueDays,
      trialDays: planForm.trialDays,
      organizationId,
    };
  }

  private planToUpdateRequest(planForm: PlanForm): {
    name?: string;
    description?: string;
    price?: PriceProto.common.v1.Price;
    cycle?: PlanProto.plans.v1.Cycle;
    visibility?: PlanProto.plans.v1.Visibility;
    status?: PlanProto.plans.v1.Status;
    products?: PlanProto.plans.v1.PlanProdRequest[];
    paymentMethods?: PlanProto.plans.v1.PaymentMethod;
    autoRenewal?: boolean;
    maxChargeAttempts?: number;
    overdueDays?: number;
    trialDays?: number;
    organizationId?: string;
  } {
    const prods = this.getUpdateProducts(planForm.products);
    return {
      name: planForm.name,
      description: planForm.description,
      price: new PriceProto.common.v1.Price({
        amount: Math.round(planForm.price * 100), // Convert to cents
        currency: "BRL",
      }),
      cycle: this.getCycle(planForm.cycle),
      visibility: this.getVisibility(planForm.visibility),
      status: this.getStatus(planForm.status),
      products: prods,
      paymentMethods: this.getPaymentMethods(planForm.paymentMethods),
      autoRenewal: planForm.autoRenewal,
      maxChargeAttempts: planForm.maxChargeAttempts,
      overdueDays: planForm.overdueDays,
      trialDays: planForm.trialDays,
      organizationId,
    };
  }

  private getCycle(cycle: string): PlanProto.plans.v1.Cycle {
    const cycleMap = {
      weekly: PlanProto.plans.v1.Cycle.CYCLE_WEEKLY,
      biweekly: PlanProto.plans.v1.Cycle.CYCLE_BIWEEKLY,
      monthly: PlanProto.plans.v1.Cycle.CYCLE_MONTHLY,
      yearly: PlanProto.plans.v1.Cycle.CYCLE_YEARLY,
    };
    return cycleMap[cycle] || PlanProto.plans.v1.Cycle.CYCLE_UNSPECIFIED;
  }

  private getVisibility(visibility: string): PlanProto.plans.v1.Visibility {
    return visibility === "public"
      ? PlanProto.plans.v1.Visibility.VISIBILITY_PUBLIC
      : PlanProto.plans.v1.Visibility.VISIBILITY_PRIVATE;
  }

  private getStatus(status: string): PlanProto.plans.v1.Status {
    return status === "active" ? PlanProto.plans.v1.Status.STATUS_PUBLISHED : PlanProto.plans.v1.Status.STATUS_DRAFT;
  }

  private getProducts(products: PlanProto.plans.v1.PlanProduct[]): PlanProto.plans.v1.PlanProdRequest[] {
    return products.map((product) => {
      return new PlanProto.plans.v1.PlanProdRequest({
        productId: product.productId,
        rules: new PlanProto.plans.v1.Rule({
          maxArticles: product.rules?.maxArticles,
          maxUsers: product.rules?.maxUsers,
        }),
        active: product.isActive,
        resources: product.resources?.map((r) => {
          return new PlanProto.plans.v1.Resource({
            resourceId: r.resourceId,
          });
        }),
      });
    });
  }

  private getUpdateProducts(products: PlanProto.plans.v1.PlanProduct[]): PlanProto.plans.v1.PlanProdRequest[] {
    return products.map((product) => {
      const resources = product.resources?.map((r) => {
        return new PlanProto.plans.v1.Resource({
          resourceId: r.resourceId,
        });
      });
      return new PlanProto.plans.v1.PlanProdRequest({
        productId: product.productId,
        rules: new PlanProto.plans.v1.Rule({
          maxArticles: product.rules?.maxArticles,
          maxUsers: product.rules?.maxUsers,
        }),
        active: product.isActive,
        resources: resources,
      });
    });
  }

  private getPaymentMethods(paymentMethods: {
    pix: boolean;
    creditCard: boolean;
    invoice: boolean;
    debit: boolean;
  }): PlanProto.plans.v1.PaymentMethod {
    return new PlanProto.plans.v1.PaymentMethod({
      pixPaymentMethod: new PlanProto.plans.v1.PixPaymentMethod({
        enable: paymentMethods.pix,
      }),
      creditPaymentMethod: new PlanProto.plans.v1.CreditPaymentMethod({
        enable: paymentMethods.creditCard,
      }),
      bankSlipPaymentMethod: new PlanProto.plans.v1.BankSlipPaymentMethod({
        enable: paymentMethods.invoice,
      }),
      debitPaymentMethod: new PlanProto.plans.v1.DebitPaymentMethod({
        enable: paymentMethods.debit,
      }),
    });
  }

  submitLeadInquiry(data: {
    firstName: string;
    lastName: string;
    corporateEmail: string;
    companyName: string;
    companySize: SubscriptionsProto.subscriptions.v1.CompanySize;
    phoneNumber: string;
  }) {
    const request = new SubscriptionsProto.subscriptions.v1.SubmitLeadInquiryRequest({
      firstName: data.firstName,
      lastName: data.lastName,
      corporateEmail: data.corporateEmail,
      companyName: data.companyName,
      companySize: data.companySize,
      phoneNumber: data.phoneNumber,
    });
    return this.clientSubscription.SubmitLeadInquiry(request, {});
  }
}
